import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { camelToSnakeCase } from 'utils/caseConversion';
import { useGovernmentOfOntarioFlag } from 'utils/featureFlags';
import {
  ONTARIO_ASSETS_TAB_SEEDS,
  ONTARIO_GTA_SYMBOLOGY,
  formatOntarioFacilityLabel,
  isOntarioGtaResourceSet,
  ontarioChecklistsForAutomationRules,
  ontarioChecklistsForThreats,
  ontarioFacilityIdFromResourceNotes,
  ontarioGtaResourceNotesMarker,
  type OntarioAssetsTabSeed,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/ontarioSnowstormNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  keys as resourceSetKeys,
  mapResourceSetToEditPayload,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';

const ONTARIO_RESOURCE_KIND = 'Facilities';
const ONTARIO_RESOURCE_TYPE = 'Ontario Facility';

function checklistItemsMatch(
  actual: OrgEventChecklistItemDef[] | undefined,
  expected: OrgEventChecklistItemDef[]
): boolean {
  if (!Array.isArray(actual) || actual.length !== expected.length) {
    return false;
  }
  return expected.every((item, index) => {
    const current = actual[index];
    return (
      current?.id === item.id &&
      current?.kind === item.kind &&
      current?.label === item.label
    );
  });
}

function seedMatchesResource(
  resource: ResourceSet,
  seed: OntarioAssetsTabSeed
): boolean {
  const threats = Array.isArray(resource.threats) ? resource.threats : [];
  if (
    threats.length !== seed.threats.length ||
    !seed.threats.every((threat) => threats.includes(threat))
  ) {
    return false;
  }
  const expectedThreatChecklists = ontarioChecklistsForThreats(seed.threats);
  const expectedRuleChecklists = ontarioChecklistsForAutomationRules(
    seed.threats
  );
  const ruleNames = expectedRuleChecklists.automationRules;
  const storedRules = Array.isArray(resource.automation_rules)
    ? resource.automation_rules
    : [];
  if (
    storedRules.length !== ruleNames.length ||
    !ruleNames.every((name) => storedRules.includes(name))
  ) {
    return false;
  }
  const threatsMatch = seed.threats.every((threat) =>
    checklistItemsMatch(
      resource.threat_checklists?.[threat],
      expectedThreatChecklists[threat] ?? []
    )
  );
  const rulesMatch = ruleNames.every((name) =>
    checklistItemsMatch(
      resource.automation_rule_checklists?.[name],
      expectedRuleChecklists.automationRuleChecklists[name] ?? []
    )
  );
  return threatsMatch && rulesMatch;
}

const ensureLocks = new Map<string, Promise<unknown>>();

async function patchStaleOntarioSeeds({
  resources,
  groupGuid,
  userGuid,
}: {
  resources: ResourceSet[];
  groupGuid: string;
  userGuid: string;
}): Promise<boolean> {
  const seedsById = new Map(
    ONTARIO_ASSETS_TAB_SEEDS.map((seed) => [seed.id, seed])
  );
  const stale = resources.filter((resource) => {
    if (!isOntarioGtaResourceSet(resource)) return false;
    const seed = seedsById.get(
      ontarioFacilityIdFromResourceNotes(resource.notes) ?? ''
    );
    if (!seed) return !resource.symbology;
    return !resource.symbology || !seedMatchesResource(resource, seed);
  });
  if (stale.length === 0) return false;

  await Promise.all(
    stale.map((resource) => {
      const seed = seedsById.get(
        ontarioFacilityIdFromResourceNotes(resource.notes) ?? ''
      );
      const threats = seed?.threats ?? resource.threats ?? [];
      const ruleSeed = seed
        ? ontarioChecklistsForAutomationRules(seed.threats)
        : {
            automationRules: resource.automation_rules ?? [],
            automationRuleChecklists: resource.automation_rule_checklists ?? {},
          };
      const threatChecklists = seed
        ? {
            ...(resource.threat_checklists ?? {}),
            ...ontarioChecklistsForThreats(seed.threats),
          }
        : (resource.threat_checklists ?? {});
      const automationRuleChecklists = seed
        ? {
            ...(resource.automation_rule_checklists ?? {}),
            ...ruleSeed.automationRuleChecklists,
          }
        : (resource.automation_rule_checklists ?? {});
      const payload = {
        ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
        symbology: resource.symbology || ONTARIO_GTA_SYMBOLOGY,
        threats,
        threatChecklists,
        automationRules: ruleSeed.automationRules,
        automationRuleChecklists,
      };
      const {
        members: _members,
        threatChecklists: checklists,
        automationRuleChecklists: ruleChecklists,
        ...dataForBackend
      } = payload;
      return axios.put(
        `${config.apiGateway.ics_resource_sets}/${resource.resource_set_id}`,
        {
          ...camelToSnakeCase(dataForBackend),
          threat_checklists: checklists,
          automation_rule_checklists: ruleChecklists,
          user_guid: userGuid,
          group_guid: groupGuid,
          incident_id: null,
        }
      );
    })
  );
  return true;
}

async function ensureOntarioGtaResourceSetsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<ResourceSet[]>(
    `${config.apiGateway.ics_resource_sets}/${groupGuid}`
  );
  const existing = resp.data ?? [];
  const existingMarkers = new Set(
    existing
      .filter(isOntarioGtaResourceSet)
      .map((resource) => resource.notes as string)
  );

  const toCreate = ONTARIO_ASSETS_TAB_SEEDS.filter(
    (facility) =>
      !existingMarkers.has(ontarioGtaResourceNotesMarker(facility.id))
  );

  let didMutate = false;

  if (toCreate.length > 0) {
    await Promise.all(
      toCreate.map((facility) => {
        const notes = ontarioGtaResourceNotesMarker(facility.id);
        return axios.post(config.apiGateway.ics_resource_sets, {
          name: formatOntarioFacilityLabel(facility),
          description: `${formatOntarioFacilityLabel(facility)} — Government of Ontario facility inside the winter storm warning area.`,
          kind: ONTARIO_RESOURCE_KIND,
          type: ONTARIO_RESOURCE_TYPE,
          quantity: 1,
          status: 'In Organization',
          asset_status: 'PMC',
          asset_status_timestamp: new Date().toISOString(),
          address: `${facility.city}, ${facility.state}`,
          symbology: ONTARIO_GTA_SYMBOLOGY,
          threats: facility.threats,
          threat_checklists: ontarioChecklistsForThreats(facility.threats),
          automation_rules: ontarioChecklistsForAutomationRules(facility.threats)
            .automationRules,
          automation_rule_checklists: ontarioChecklistsForAutomationRules(
            facility.threats
          ).automationRuleChecklists,
          notes,
          geometry: {
            type: 'Point',
            coordinates: [facility.lon, facility.lat],
          },
          incidents: [],
          archived: false,
          group_guid: groupGuid,
          user_guid: userGuid,
          incident_id: null,
        });
      })
    );
    didMutate = true;
  }

  const patched = await patchStaleOntarioSeeds({
    resources: existing,
    groupGuid,
    userGuid,
  });
  if (patched) didMutate = true;

  if (didMutate) {
    await queryClient.invalidateQueries({
      queryKey: resourceSetKeys.all,
    });
  }
}

export function useEnsureOntarioGtaResourceSets() {
  const ontarioEnabled = useGovernmentOfOntarioFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ontarioEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureOntarioGtaResourceSetsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[Ontario] Failed to ensure GTA resource sets',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [ontarioEnabled, groupGuid, userGuid, queryClient]);
}
